import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  memo,
  useCallback,
} from "react";
import { employmentFields } from "./ProfileData";
import {
  getPreviousEmployment,
  updatePreviousEmployment,
} from "../../../ApiHelper/url_Helpers.js";
import Loader from "../../../Components/Loader";
import { Plus, Trash } from "lucide-react";
import { PulseLoader, RiseLoader } from "react-spinners";

// --- Form Field Component with ReadOnly Logic ---
const FormField = memo(({ field, value, onChange, index, readOnly }) => {
  const commonClass = `border rounded-lg px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm w-full outline-none transition-all ${
    readOnly
      ? "bg-gray-100 cursor-not-allowed text-gray-500"
      : "bg-[#FAFBFD] border-gray-300 focus:ring-1 focus:ring-[#00B8D9]"
  }`;

  return (
    <div className="flex flex-col justify-start p-1.5 md:p-4 gap-1.5 md:gap-2 w-full min-w-0">
      <label
        htmlFor={`${field.id}-${index}`}
        className="text-[14px] md:text-sm font-semibold text-gray-700"
      >
        {field.label}
        {field.required && !readOnly && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>
      {field.type === "select" ? (
        <select
          id={`${field.id}-${index}`}
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => onChange(index, field.id, e.target.value)}
          className={commonClass}
        >
          <option value="">Select</option>
          {field.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={`${field.id}-${index}`}
          type={field.type}
          value={value || ""}
          readOnly={readOnly}
          onChange={(e) => onChange(index, field.id, e.target.value)}
          placeholder={field.placeholder}
          className={commonClass}
        />
      )}
    </div>
  );
});

const PreviousEmployment = forwardRef(({ onEditStatusChange, onDirtyChange }, ref) => {
  const [employmentList, setEmploymentList] = useState([{ id: Date.now() }]);
  const [initialList, setInitialList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    if (onDirtyChange) {
      const isDirty = JSON.stringify(initialList) !== JSON.stringify(employmentList);
      onDirtyChange(isDirty);
    }
  }, [employmentList, initialList, onDirtyChange]);

  // 1. GET API integration
  const fetchEmploymentData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPreviousEmployment();

      const section = response?.previous_employments || {};
      const canEdit = section.action?.isEdit ?? false;
      const data = section.data || [];

      setIsEditable(canEdit);
      onEditStatusChange(canEdit);
      if (data.length > 0) {
        setEmploymentList(data);
        setInitialList(JSON.parse(JSON.stringify(data)));
      } else {
        const defaultList = [{ id: Date.now() }];
        setEmploymentList(defaultList);
        setInitialList(JSON.parse(JSON.stringify(defaultList)));
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    fetchEmploymentData();
  }, [fetchEmploymentData]);

  // --- Expose State and Save Function to Parent ---
  useImperativeHandle(ref, () => ({
    isEditable: isEditable,
    handleChildSave: async () => {
      if (!isEditable) return { success: false, error: "Editing locked" };

      try {
        const payload = employmentList.map(({ id, ...rest }) => rest);
        await updatePreviousEmployment(payload);
        setIsEditable(false);
        await fetchEmploymentData();

        return { success: true };
      } catch (err) {
        const errMsg =
          err.response?.data?.message || "Failed to update employment";
        console.error("Save Error:", err.response?.data);
        return { success: false, error: errMsg };
      }
    },
  }));

  const handleInputChange = (index, id, value) => {
    if (!isEditable) return;
    const updatedList = [...employmentList];
    updatedList[index] = { ...updatedList[index], [id]: value };
    setEmploymentList(updatedList);
  };

  const addEmployment = () => {
    if (!isEditable) return;
    setEmploymentList((prev) => [...prev, { id: Date.now() }]);
  };

  const handleDeleteEmployment = (index) => {
    if (!isEditable || employmentList.length <= 1) return;
    setEmploymentList((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden min-w-0 pb-10">
      {loading ? (
        <div
          className={`flex items-center justify-center w-full h-full min-h-[350px] bg-white`}
        >
          <RiseLoader color="#35e6e9" />
        </div>
      ) : (
        employmentList.map((job, i) => (
          <div
            key={job.id || i}
            className="border-black/5 border-2 bg-white rounded-lg mb-8 md:mb-8 shadow-sm overflow-hidden w-full min-w-0"
          >
            <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50/50">
              <h3 className="font-semibold text-black text-xs md:text-sm">
                Employment {i + 1}
              </h3>
              {/* Delete button if more than one employment and editable */}
              {isEditable && employmentList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleDeleteEmployment(i)}
                  className="ml-2 text-red-500 hover:text-red-700 text-xs md:text-sm font-semibold px-2 py-1 border border-red-200 rounded transition-colors"
                  title="Delete this employment entry"
                >
                  <Trash size={12} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full min-w-0">
              {employmentFields.map((field) => (
                <FormField
                  key={field.id}
                  index={i}
                  field={field}
                  value={job[field.id]}
                  onChange={handleInputChange}
                  readOnly={!isEditable}
                />
              ))}
            </div>
            {isEditable && i === employmentList.length - 1 && (
              <button
                type="button"
                onClick={addEmployment}
                className="flex justify-center items-center gap-2 text-xs md:text-[14px] font-bold text-[#00B8D9] py-3 md:py-4 w-full hover:bg-cyan-50 transition-colors border-t border-dashed"
              >
                <Plus size={18} /> Add Another Employment
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
});

export default PreviousEmployment;
